import { useEffect, useState } from "react"

import { fetchAttachmentBlob } from "../api/services.ts"

/**
 * An object URL for an attachment, fetched with the caller's credentials.
 *
 * The download route is JWT-protected, so the browser cannot fetch it on its
 * own: an `<img src>` or `<a href>` sends no Authorization header. Pulling the
 * bytes through the API client and wrapping them in an object URL is what makes
 * a preview and a download work without loosening the permission on the file.
 *
 * `enabled` keeps this from fetching every attachment in a long conversation —
 * only images need the bytes up front; a document fetches when it is asked for.
 */
export function useAttachmentBytes(
  attachmentId: string,
  {
    enabled = true,
    portal = false
  }: { enabled?: boolean; portal?: boolean } = {}
) {
  const [url, setUrl] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    if (!enabled) return
    let revoked = false
    let objectUrl: string | null = null

    const load = async () => {
      setFailed(false)
      const response = await fetchAttachmentBlob(attachmentId, portal)
      if (revoked) return
      if (!response.ok || !response.data) {
        setFailed(true)
        return
      }
      objectUrl = URL.createObjectURL(response.data)
      setUrl(objectUrl)
    }
    load()

    return () => {
      revoked = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [attachmentId, enabled, portal])

  return { url, failed }
}
